'use client';

import { useEffect, useRef, useState } from 'react';
import { Link as LinkIcon, Unlink } from 'lucide-react';
import { type Editor } from '@tiptap/react';
import { NodeSelection } from '@tiptap/pm/state';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover/popover';
import { Input } from '@/components/ui/input/input';
import { Button } from '@/components/ui/button/button';
import { ToolbarButton } from './toolbar-button';

interface LinkPopoverProps {
  editor: Editor;
  className?: string;
}

// Images can't carry the link mark (block node, no marks in the schema), so
// links on images live in the node's `href` attribute instead. Read the
// selection at call time — the toolbar doesn't re-render on selection-only
// transactions, so render-time values can be stale.
function isImageSelected(editor: Editor): boolean {
  const { selection } = editor.state;
  return selection instanceof NodeSelection && selection.node.type.name === 'image';
}

export function LinkPopover({ editor, className }: LinkPopoverProps) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isActive =
    editor.isActive('link') || (isImageSelected(editor) && !!editor.getAttributes('image').href);

  useEffect(() => {
    if (open) {
      const existing = isImageSelected(editor)
        ? (editor.getAttributes('image').href ?? '')
        : (editor.getAttributes('link').href ?? '');
      setUrl(existing);
      // Focus input after popover animation
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open, editor]);

  function applyLink() {
    const trimmed = url.trim();
    if (isImageSelected(editor)) {
      editor
        .chain()
        .focus()
        .updateAttributes('image', { href: trimmed || null })
        .run();
    } else if (!trimmed) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run();
    }
    setOpen(false);
  }

  function removeLink() {
    if (isImageSelected(editor)) {
      editor.chain().focus().updateAttributes('image', { href: null }).run();
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }
    setOpen(false);
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <span>
            <ToolbarButton
              onClick={() => setOpen((o) => !o)}
              isActive={isActive}
              tooltip="Add link"
              className={className}
            >
              <LinkIcon className="h-4 w-4" />
            </ToolbarButton>
          </span>
        </PopoverTrigger>
        <PopoverContent
          className="w-80 p-3"
          side="bottom"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">URL</label>
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyLink();
                  }
                  if (e.key === 'Escape') {
                    setOpen(false);
                  }
                }}
                placeholder="https://"
                className="h-8 text-sm"
              />
              <Button type="button" size="sm" className="h-8 px-3" onClick={applyLink}>
                Apply
              </Button>
            </div>
            {isActive && (
              <button
                type="button"
                onClick={removeLink}
                className="flex items-center gap-1 text-xs text-destructive hover:underline"
              >
                <Unlink className="h-3 w-3" />
                Remove link
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
}
